import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';

/**
 * Global imperative confirm dialog — replaces window.confirm().
 *
 * Usage:
 *   import { confirm } from '@/components/ui/confirm-dialog';
 *
 *   const ok = await confirm({
 *     title: 'Delete account?',
 *     description: 'This action cannot be undone.',
 *     confirmText: 'Delete',
 *     variant: 'destructive',
 *   });
 *   if (!ok) return;
 *
 * Typed-confirmation (high-stakes destructive actions):
 *   const ok = await confirm({
 *     title: 'Delete workflow?',
 *     description: '12 accounts are currently enrolled.',
 *     confirmText: 'Delete workflow',
 *     variant: 'destructive',
 *     requireText: 'Renewal Risk',         // user must type this exactly
 *     requireTextLabel: 'Type the workflow name to confirm',
 *   });
 */

let pushDialog = null;

/**
 * Show a confirmation dialog and return a Promise<boolean>.
 *
 * @param {Object} options
 * @param {string} options.title - Dialog title.
 * @param {string} options.description - Dialog description / body text.
 * @param {string} [options.confirmText='Continue'] - Label for the confirm button.
 * @param {string} [options.cancelText='Cancel'] - Label for the cancel button.
 * @param {'default'|'destructive'} [options.variant='default'] - Visual style.
 * @param {string} [options.requireText] - If set, the user must type this exact
 *   string into a confirmation field before the confirm button enables. Use for
 *   high-stakes destructive actions on resources with downstream impact
 *   (e.g. workflows with active enrollments).
 * @param {string} [options.requireTextLabel='Type to confirm'] - Label rendered
 *   above the confirmation input when `requireText` is set.
 * @returns {Promise<boolean>} Resolves true if confirmed, false if cancelled.
 */
export function confirm({
  title = 'Are you sure?',
  description = '',
  confirmText = 'Continue',
  cancelText = 'Cancel',
  variant = 'default',
  requireText = null,
  requireTextLabel = 'Type to confirm',
} = {}) {
  return new Promise((resolve) => {
    if (pushDialog) {
      pushDialog({
        title,
        description,
        confirmText,
        cancelText,
        variant,
        requireText,
        requireTextLabel,
        resolve,
      });
    } else {
      // Fallback if provider is not mounted
      resolve(window.confirm(`${title}\n\n${description}`));
    }
  });
}

/**
 * Mount this component once at the app root, alongside <Toaster />.
 *
 *   <ConfirmDialogProvider />
 */
export function ConfirmDialogProvider() {
  const [dialog, setDialog] = useState(null);
  const [typedValue, setTypedValue] = useState('');

  useEffect(() => {
    pushDialog = (d) => {
      setTypedValue('');
      setDialog(d);
    };
    return () => { pushDialog = null; };
  }, []);

  const handleConfirm = useCallback(() => {
    dialog?.resolve(true);
    setDialog(null);
    setTypedValue('');
  }, [dialog]);

  const handleCancel = useCallback(() => {
    dialog?.resolve(false);
    setDialog(null);
    setTypedValue('');
  }, [dialog]);

  if (!dialog) return null;

  const requiresTyping = Boolean(dialog.requireText);
  const typingMatches = !requiresTyping || typedValue === dialog.requireText;

  return (
    <AlertDialog open onOpenChange={(open) => { if (!open) handleCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{dialog.title}</AlertDialogTitle>
          {dialog.description && (
            <AlertDialogDescription className="whitespace-pre-line">
              {dialog.description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        {requiresTyping && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {dialog.requireTextLabel}{' '}
              <code className="px-1 py-0.5 rounded bg-muted text-xs">
                {dialog.requireText}
              </code>
            </label>
            <Input
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              placeholder={dialog.requireText}
              autoFocus
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel}>
            {dialog.cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!typingMatches}
            className={
              dialog.variant === 'destructive'
                ? 'bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed'
                : 'disabled:opacity-50 disabled:cursor-not-allowed'
            }
          >
            {dialog.confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
